import { ORG_UNIT_TYPES, PROVINCES } from '@clairo/domain';
import { Building2, Plus, ShieldHalf } from 'lucide-react';
import { motion } from 'motion/react';
import { useMemo, useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { useCreateOrgUnit, useOrgUnits, useSession } from '../auth/session';
import { Skeleton } from '../components/feedback/Progress';
import { toastPromise } from '../components/feedback/toast';
import { Button } from '../components/ui/Button';
import { Badge, Card } from '../components/ui/Card';
import { Dialog } from '../components/ui/Dialog';
import { SelectField, TextField } from '../components/ui/Field';
import type { OrgUnit } from '../lib/api';

// The org tree for the current context (docs/architecture.md §8), and adding units to it.
// Creating one shows a single toast that follows the request from start to finish.

export function OrganizationPage() {
  const { t } = useTranslation();
  const { org, membership } = useSession();
  const units = useOrgUnits(org);
  const [open, setOpen] = useState(false);
  const canCreate = membership?.role === 'owner' || membership?.role === 'admin';

  const tree = useMemo(() => flatten(units.data ?? []), [units.data]);

  return (
    <div className="mx-auto flex max-w-[1600px] flex-col gap-6">
      <header className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="text-3xl font-extrabold text-text">{t('org.title')}</h1>
          <p className="mt-1 max-w-2xl text-md text-muted">{t('org.subtitle')}</p>
        </div>
        {canCreate && (
          <Button
            icon={<Plus size={17} />}
            onClick={() => setOpen(true)}
            disabled={!units.data?.length}
          >
            {t('org.add')}
          </Button>
        )}
      </header>

      <Card className="p-3 sm:p-4">
        {units.isLoading ? (
          <div className="flex flex-col gap-2 p-2">
            {[0, 1, 2, 3, 4].map((i) => (
              <Skeleton key={i} className="h-12" />
            ))}
          </div>
        ) : tree.length === 0 ? (
          <p className="p-10 text-center text-muted">{t('org.empty')}</p>
        ) : (
          <ul role="tree" aria-label={t('org.title')} className="flex flex-col">
            {tree.map(({ unit, depth }, i) => (
              <motion.li
                key={unit.id}
                role="treeitem"
                aria-level={depth + 1}
                layout
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.04 }}
                className="group flex items-center gap-3 rounded-md py-2.5 pr-3 hover:bg-subtle"
                style={{ paddingLeft: 12 + depth * 28 }}
              >
                {depth > 0 && <span className="h-px w-4 bg-border" aria-hidden="true" />}
                <span className="grid h-9 w-9 shrink-0 place-items-center rounded-md bg-gradient-to-br from-primary/15 to-accent/15 text-primary">
                  <Building2 size={16} />
                </span>
                <span className="min-w-0 flex-1">
                  <span className="block truncate font-semibold text-text">{unit.name}</span>
                  <span className="block text-xs text-muted">
                    {t(`org.types.${unit.type}`, { defaultValue: unit.type })}
                    {unit.province ? ` · ${unit.province}` : ''}
                  </span>
                </span>
                {unit.isCustodian && (
                  <Badge tone="accent">
                    <ShieldHalf size={11} /> {t('org.custodianBadge')}
                  </Badge>
                )}
              </motion.li>
            ))}
          </ul>
        )}
      </Card>

      <AddOrgUnitDialog open={open} onOpenChange={setOpen} units={units.data ?? []} />
    </div>
  );
}

function AddOrgUnitDialog({
  open,
  onOpenChange,
  units,
}: {
  open: boolean;
  onOpenChange: (o: boolean) => void;
  units: OrgUnit[];
}) {
  const { t } = useTranslation();
  const { org } = useSession();
  const create = useCreateOrgUnit(org);
  const [name, setName] = useState('');
  const [type, setType] = useState('branch');
  const [parentId, setParentId] = useState('');
  const [province, setProvince] = useState('NL');
  const [custodian, setCustodian] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const parent = parentId || units[0]?.id || '';

  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (!name.trim()) return setError(t('errors.bad_request'));
    setError(null);
    void toastPromise(
      create.mutateAsync({
        name: name.trim(),
        type,
        parentId: parent,
        province,
        isCustodian: custodian,
      }),
      {
        loading: t('org.toast.creating', { name: name.trim() }),
        success: (u) => t('org.toast.created', { name: u.name }),
        description: t('org.toast.createdBody'),
      },
    )
      .then(() => {
        onOpenChange(false);
        setName('');
        setCustodian(false);
      })
      .catch(() => undefined);
  };

  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
      title={t('org.dialog.title')}
      footer={
        <>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>
            {t('common.cancel')}
          </Button>
          <Button type="submit" form="add-org-unit" loading={create.isPending}>
            {t('org.dialog.submit')}
          </Button>
        </>
      }
    >
      <form id="add-org-unit" onSubmit={submit} className="flex flex-col gap-4">
        <TextField
          label={t('org.dialog.name')}
          placeholder={t('org.dialog.namePlaceholder')}
          value={name}
          onChange={(e) => setName(e.target.value)}
          error={error}
          autoFocus
        />
        <div className="grid gap-4 sm:grid-cols-2">
          <SelectField
            label={t('org.dialog.type')}
            value={type}
            onChange={(e) => setType(e.target.value)}
            options={ORG_UNIT_TYPES.map((v) => ({ value: v, label: t(`org.types.${v}`) }))}
          />
          <SelectField
            label={t('org.dialog.province')}
            value={province}
            onChange={(e) => setProvince(e.target.value)}
            options={PROVINCES.map((p) => ({ value: p, label: p }))}
          />
        </div>
        <SelectField
          label={t('org.dialog.parent')}
          value={parent}
          onChange={(e) => setParentId(e.target.value)}
          options={units.map((u) => ({ value: u.id, label: u.name }))}
        />
        <label className="flex cursor-pointer items-start gap-3 rounded-md border border-border p-3 hover:bg-subtle">
          <input
            type="checkbox"
            checked={custodian}
            onChange={(e) => setCustodian(e.target.checked)}
            className="mt-1 h-4 w-4 accent-[var(--primary)]"
          />
          <span>
            <span className="block text-sm font-semibold text-text">
              {t('org.dialog.custodian')}
            </span>
            <span className="block text-xs text-muted">{t('org.dialog.custodianHint')}</span>
          </span>
        </label>
      </form>
    </Dialog>
  );
}

/** Units in tree order with depth, from the flat list the API returns (ordered by path). */
function flatten(units: OrgUnit[]): { unit: OrgUnit; depth: number }[] {
  const byParent = new Map<string | null, OrgUnit[]>();
  const ids = new Set(units.map((u) => u.id));
  for (const u of units) {
    const key = u.parentId && ids.has(u.parentId) ? u.parentId : null;
    byParent.set(key, [...(byParent.get(key) ?? []), u]);
  }
  const out: { unit: OrgUnit; depth: number }[] = [];
  const walk = (parent: string | null, depth: number) => {
    for (const u of byParent.get(parent) ?? []) {
      out.push({ unit: u, depth });
      walk(u.id, depth + 1);
    }
  };
  walk(null, 0);
  return out;
}
